import React, { useRef, useState } from "react";
import { Button, CircularProgress, Dialog, TextField, Typography } from "@material-ui/core";
import { ChevronRight, CreditCard, FileCopyOutlined } from "@material-ui/icons";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import Utils from "~/helpers/Utils";
import businessService from "~/services/businessService";
import bookingTransactionService, { getOneBookingTransaction } from "~/services/bookingTransactionService";
import payPaymentService from "~/services/payPaymentService";
import { usePackagePaymentModalContext } from "~/contexts/PackagePaymentModalContext";
import ButtonDefault from "~/components/common/ButtonDefault";
import DialogAddCard from "~/components/common/dialog/dialogAddCard";

const BRAND = "#8C1837";
const BG = "#FFFFFF";
const CARD_BG = "#FAFAFA";
const BORDER = "#EBEBEB";
const TEXT = "#1A1A1A";
const TEXT_SECONDARY = "#888888";

// ── Ícone PIX simples ─────────────────────────────────────────────────────────
const PixIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="white">
		<path d="M17.06 11.57c-.46 0-.9-.18-1.22-.51l-2.93-2.93c-.21-.21-.49-.32-.78-.32-.29 0-.57.11-.78.32l-2.94 2.94c-.32.32-.76.5-1.22.5h-.55l3.7 3.7c1.04 1.04 2.73 1.04 3.77 0l3.71-3.71h-.56zM6.94 12.43c.46 0 .9.18 1.22.51l2.93 2.93c.21.21.49.32.78.32.3 0 .57-.11.78-.32l2.94-2.94c.32-.32.76-.5 1.22-.5h.38l-3.7-3.7c-1.04-1.04-2.73-1.04-3.77 0l-3.7 3.7h.92zM20.82 9.71l-1.8-1.8c-.34.21-.73.32-1.13.32H17c-.29 0-.57.11-.78.32l-2.94 2.94c-.32.32-.76.51-1.22.51-.46 0-.9-.18-1.22-.51L7.9 8.55c-.21-.21-.49-.32-.78-.32h-.96c-.4 0-.79-.12-1.12-.32L3.18 9.71c-.9.9-.9 2.36.01 3.26l1.8 1.8c.33-.2.72-.32 1.12-.32h.96c.29 0 .57-.11.78-.32l2.94-2.94c.64-.64 1.8-.64 2.44 0l2.93 2.93c.21.21.49.32.78.32h.89c.4 0 .79.12 1.13.32l1.8-1.8c.92-.9.92-2.35.06-3.25z" />
	</svg>
);


const PackagePaymentModal = () => {
	const history = useHistory();
	const { modalState, closePaymentModal } = usePackagePaymentModalContext();
	const { open, bookingTransactionId, totalPaid, currency, title, onSuccess } = modalState;

	const { user } = useSelector((store) => store.auth);
	const { business } = useSelector((store) => store.business);
	const meStore = useSelector((store) => store.me?.me);
	const me = Utils.safeParseJSON("@prit-web/meData", meStore);

	const [step, setStep] = useState("method"); // method | card | pix | processing | pix_code | confirmed
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [pixCode, setPixCode] = useState(null);
	const [hasCopied, setHasCopied] = useState(false);
	const pixIntervalRef = useRef(null);
	const confirmTimerRef = useRef(null);

	// dados cartão (preenchidos pelo DialogAddCard)
	const cardDataRef = useRef(null);

	// campo pix
	const [pixCpf, setPixCpf] = useState("");

	const resetState = () => {
		setStep("method");
		setLoading(false);
		setError("");
		setPixCode(null);
		setHasCopied(false);
		cardDataRef.current = null;
		setPixCpf("");
		if (pixIntervalRef.current) {
			clearInterval(pixIntervalRef.current);
			pixIntervalRef.current = null;
		}
		if (confirmTimerRef.current) {
			clearTimeout(confirmTimerRef.current);
			confirmTimerRef.current = null;
		}
	};

	const POLL_INTERVAL_MS = 3000;
	const POLL_TIMEOUT_MS  = 5 * 60 * 1000; // 5 minutos

	const startPolling = () => {
		const startedAt = Date.now();

		pixIntervalRef.current = setInterval(async () => {
			try {
				const txRes = await getOneBookingTransaction(bookingTransactionId);
				const status = txRes.data?.paymentStatus;

				if (status === "completed" || status === "partial") {
					clearInterval(pixIntervalRef.current);
					pixIntervalRef.current = null;
					setStep("confirmed");
					confirmTimerRef.current = setTimeout(() => {
						if (onSuccess) onSuccess();
						handleClose();
					}, 2000);
					return;
				}
			} catch (_) {}

			// Timeout de segurança: para de pender após 5 min
			if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
				clearInterval(pixIntervalRef.current);
				pixIntervalRef.current = null;
				setError("Tempo de confirmação esgotado. Verifique o status do pagamento.");
				setStep("card");
			}
		}, POLL_INTERVAL_MS);
	};

	const handleClose = () => {
		resetState();
		closePaymentModal();
	};

	const cpfMask = (value) =>
		value.replace(/\D/g, "")
			.replace(/(\d{3})(\d)/, "$1.$2")
			.replace(/(\d{3})(\d)/, "$1.$2")
			.replace(/(\d{3})(\d{1,2})/, "$1-$2")
			.replace(/(-\d{2})\d+?$/, "$1");

	const buildClient = (meData, address, document) => ({
		id: user?.id || me?.id || localStorage.getItem("@prit-web/userId") || meData?.id,
		name: me?.name || user?.displayName || cardDataRef.current?.name || Utils.safeLocalGet("@prit-web/userName") || meData?.name || "Usuário Prit",
		email: user?.email || cardDataRef.current?.email || me?.email || meData?.email || "contato@prit.app",
		country: "br",
		documents: [document],
		phone_numbers: me?.phone ? [me.phone] : meData?.phone ? [meData.phone] : [meData?.phoneUnverified || ""],
		address: {
			street: address?.street || "Av. Prof. João Fiúsa",
			complementary: address?.unit || "",
			street_number: address?.number || "1901",
			neighborhood: address?.neighborhood || "Jardim Botânico",
			city: address?.city || "Ribeirão Preto",
			state: address?.state || "SP",
			zipcode: address?.postalCode || "14024250",
			country: "br",
		},
	});

	const submitPayment = async (isPix, document, cardPayload) => {
		setLoading(true);
		setError("");
		try {
			const meData = JSON.parse(localStorage.getItem("@prit-web/meData") || "{}");
			const res = await businessService.getBusiness(business.id);
			const businessUpdated = res.data;
			const postalCode = businessUpdated?.address?.postalCode?.trim();

			if (
				businessUpdated.phone.startsWith("+55") &&
				postalCode &&
				!/^\d{8}$/.test(postalCode.replace("-", ""))
			) {
				setError("Endereço do estabelecimento inválido. Tente novamente.");
				setLoading(false);
				return;
			}

			const address = businessUpdated.address || business.address;

			const payload = {
				booking_external_id: bookingTransactionId,
				recipient_id: business.id,
				card: cardPayload || null,
				client: buildClient(meData, address, document),
				amount: totalPaid / 100,
				discount: 0,
				payment_type: isPix ? 5 : null,
			};

			const paymentRes = await payPaymentService.postPaymentPay(payload);

			if (isPix) {
				// Mostra QR code, publica e inicia polling
				setPixCode(paymentRes.data.pix_qr_code);
				setStep("pix_code");
				setLoading(false);
				bookingTransactionService.publishBooktranscation(bookingTransactionId).catch(() => {});
				startPolling();
			} else {
				// Cartão: já estava em processing (definido antes da chamada), publica e inicia polling
				setLoading(false);
				bookingTransactionService.publishBooktranscation(bookingTransactionId).catch(() => {});
				startPolling();
			}
		} catch (err) {
			const message = err?.response?.data?.message || err?.message || "Verifique sua conexão e tente novamente.";
			setError(message);
			setLoading(false);
			if (!isPix) setStep("card");
		}
	};

	const handleCardOpenNext = () => {
		const data = cardDataRef.current;
		if (!data) return;
		setStep("processing");
		submitPayment(false, data.cpf, {
			card_holder_name: data.name,
			card_expiration_date: data.expiry,
			card_number: data.number,
			card_cvv: data.cvc,
		});
	};

	const handlePixSubmit = () => {
		setError("");
		if (!pixCpf || !Utils.validateCPF(pixCpf.replace(/[^0-9]/g, ""))) {
			setError("CPF inválido.");
			return;
		}
		submitPayment(true, pixCpf.replace(/[^0-9]/g, ""), null);
	};

	// ── Shared header ─────────────────────────────────────────────────────────
	const renderHeader = (showBack = false, onBack = null) => (
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
			{showBack ? (
				<button
					style={{ background: "none", border: "none", cursor: "pointer", color: TEXT_SECONDARY, fontSize: 22, lineHeight: 1, padding: 0 }}
					onClick={onBack}
				>
					←
				</button>
			) : <div style={{ width: 24 }} />}
			<button
				style={{ background: "none", border: "none", cursor: "pointer", color: TEXT_SECONDARY, fontSize: 18, lineHeight: 1, padding: 0 }}
				onClick={handleClose}
			>
				✕
			</button>
		</div>
	);

	// ── Method selection ──────────────────────────────────────────────────────
	const renderMethod = () => (
		<div style={{ padding: "20px 20px 28px" }}>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
				<Typography style={{ fontSize: 18, fontWeight: 700, color: TEXT }}>
					Como você quer pagar?
				</Typography>
				<button
					style={{ background: "none", border: "none", cursor: "pointer", color: TEXT_SECONDARY, fontSize: 18, lineHeight: 1, padding: 0 }}
					onClick={handleClose}
				>
					✕
				</button>
			</div>
			<Typography style={{ fontSize: 14, color: TEXT_SECONDARY, marginBottom: 28, marginTop: -12 }}>
				{title ? `${title} · ` : ""}{Utils.formatMoney(totalPaid / 100, currency)}
			</Typography>

			{/* Cartão */}
			<div
				onClick={() => { setError(""); setStep("card"); }}
				style={{
					display: "flex", alignItems: "center", gap: 14,
					backgroundColor: CARD_BG, border: `1px solid ${BORDER}`,
					borderRadius: 14, padding: "16px 14px", marginBottom: 10, cursor: "pointer",
				}}>
				<div style={{
					width: 40, height: 40, borderRadius: 10, backgroundColor: BRAND,
					display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
				}}>
					<CreditCard style={{ color: "#fff", fontSize: 20 }} />
				</div>
				<div style={{ flex: 1 }}>
					<Typography style={{ color: TEXT, fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>
						cartão
					</Typography>
					<Typography style={{ color: TEXT_SECONDARY, fontSize: 13 }}>
						crédito ou débito, on-line
					</Typography>
				</div>
				<ChevronRight style={{ color: TEXT_SECONDARY, fontSize: 20 }} />
			</div>

			{/* PIX */}
			<div
				onClick={() => { setError(""); setStep("pix"); }}
				style={{
					display: "flex", alignItems: "center", gap: 14,
					backgroundColor: CARD_BG, border: `1px solid ${BORDER}`,
					borderRadius: 14, padding: "16px 14px", cursor: "pointer",
				}}>
				<div style={{
					width: 40, height: 40, borderRadius: 10, backgroundColor: BRAND,
					display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
				}}>
					<PixIcon />
				</div>
				<div style={{ flex: 1 }}>
					<Typography style={{ color: TEXT, fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>
						pix
					</Typography>
					<Typography style={{ color: TEXT_SECONDARY, fontSize: 13 }}>
						chave exibida na hora, no balcão
					</Typography>
				</div>
				<ChevronRight style={{ color: TEXT_SECONDARY, fontSize: 20 }} />
			</div>
		</div>
	);

	// ── Card form ─────────────────────────────────────────────────────────────
	const renderCard = () => (
		<>
			<DialogAddCard
				onClose={() => { setError(""); setStep("method"); }}
				setCardData={(data) => { cardDataRef.current = data; }}
				openNext={handleCardOpenNext}
			/>
			{error && (
				<Typography style={{ color: "#D32F2F", fontSize: 12, padding: "0 24px 16px" }}>
					{error}
				</Typography>
			)}
		</>
	);

	// ── PIX form ──────────────────────────────────────────────────────────────
	const renderPix = () => (
		<div style={{ padding: "24px" }}>
			{/* Header: título + ✕ (mesma linha — igual ao dialogPix) */}
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
				<Typography style={{ fontSize: 18, fontWeight: 600, color: "#333" }}>
					Pagar com Pix
				</Typography>
				<button
					style={{ background: "none", border: "none", fontSize: 18, color: "#999", cursor: "pointer", padding: 4, lineHeight: 1 }}
					onClick={() => { setError(""); setStep("method"); }}
				>
					&#x2715;
				</button>
			</div>

			<p style={{ color: "#666", fontSize: 14, margin: "0 0 20px" }}>
				Precisamos confirmar seu CPF para gerar o Pix do pagamento.
			</p>

			<div style={{ marginBottom: 24 }}>
				<Typography style={{ fontSize: 14, fontWeight: 600, color: "#333", marginBottom: 8 }}>
					CPF
				</Typography>
				<TextField
					placeholder="Digite seu CPF"
					name="cpf"
					fullWidth
					variant="outlined"
					size="small"
					value={pixCpf}
					onChange={(e) => setPixCpf(cpfMask(e.target.value))}
					onKeyPress={(event) => {
						if (event.key === "Enter") handlePixSubmit();
					}}
					inputProps={{ maxLength: 14, style: { fontSize: 14, borderRadius: 8 } }}
					InputProps={{ style: { borderRadius: 8 } }}
					error={!!error}
				/>
				{error && <Typography style={{ color: "#D32F2F", fontSize: 13, marginTop: 6 }}>{error}</Typography>}
			</div>

			<ButtonDefault disabled={loading} onClick={handlePixSubmit}>
				{loading ? <CircularProgress size={22} style={{ color: "#fff" }} /> : "Continuar para o Pix"}
			</ButtonDefault>
		</div>
	);

	// ── Processing ────────────────────────────────────────────────────────────
	const renderProcessing = () => (
		<div style={{ padding: "48px 20px", textAlign: "center" }}>
			<CircularProgress size={48} style={{ color: BRAND, marginBottom: 24 }} />
			<Typography style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 8 }}>
				Processando o pagamento
			</Typography>
			<Typography style={{ fontSize: 14, color: TEXT_SECONDARY }}>
				Aguarde, estamos confirmando seu pagamento...
			</Typography>
		</div>
	);

	// ── Confirmed ─────────────────────────────────────────────────────────────
	const renderConfirmed = () => (
		<div style={{ padding: "48px 20px", textAlign: "center" }}>
			<div style={{
				width: 64, height: 64, borderRadius: "50%",
				backgroundColor: "#E8F5E9",
				display: "flex", alignItems: "center", justifyContent: "center",
				margin: "0 auto 24px",
			}}>
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none">
					<path d="M20 6L9 17l-5-5" stroke="#2E7D32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</div>
			<Typography style={{ fontSize: 20, fontWeight: 700, color: TEXT, marginBottom: 8 }}>
				Pagamento confirmado!
			</Typography>
			<Typography style={{ fontSize: 14, color: TEXT_SECONDARY }}>
				Seu pacote foi pago com sucesso.
			</Typography>
		</div>
	);

	// ── PIX QR code ───────────────────────────────────────────────────────────
	const renderPixCode = () => (
		<div style={{ padding: "20px 20px 28px" }}>
			<Typography style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 8 }}>
				Pedido aguardando pagamento
			</Typography>
			<Typography style={{ color: TEXT_SECONDARY, fontSize: 14, marginBottom: 20 }}>
				1. Copie o código abaixo<br />
				2. Pague via PIX Copia e Cola em qualquer aplicativo habilitado
			</Typography>

			<div style={{
				border: `2px dotted ${BORDER}`, display: "flex", alignItems: "center",
				padding: "12px", borderRadius: 10, marginBottom: 16, gap: 10,
				backgroundColor: "#FFF5F5",
			}}>
				<Typography style={{ flex: 1, color: "#FF6B6B", fontSize: 13, wordBreak: "break-all" }}>
					{pixCode?.substring(0, 20)}...
				</Typography>
				<CopyToClipboard text={pixCode} onCopy={() => setHasCopied(true)}>
					<FileCopyOutlined style={{ color: "#3689ea", cursor: "pointer", flexShrink: 0 }} />
				</CopyToClipboard>
			</div>

			<CopyToClipboard text={pixCode} onCopy={() => setHasCopied(true)}>
				<ButtonDefault>
					{hasCopied ? "Copiado!" : "Copiar código"}
				</ButtonDefault>
			</CopyToClipboard>

			<Button
				fullWidth
				variant="outlined"
				style={{ marginTop: 10, borderRadius: 24, textTransform: "none", fontSize: 14, color: TEXT_SECONDARY, borderColor: BORDER, height: 48 }}
				onClick={handleClose}
			>
				Fechar
			</Button>
		</div>
	);

	return (
		<Dialog
			open={open}
			onClose={["pix_code", "processing", "confirmed"].includes(step) ? undefined : handleClose}
			PaperProps={{ style: { borderRadius: 30, maxHeight: step === "card" ? "none" : "60vh", maxWidth: step === "card" ? 360 : 312, backgroundColor: BG, overflow: "visible" } }}
		>
			{step === "method" && renderMethod()}
			{step === "card" && renderCard()}
			{step === "pix" && renderPix()}
			{step === "processing" && renderProcessing()}
			{step === "pix_code" && renderPixCode()}
			{step === "confirmed" && renderConfirmed()}
		</Dialog>
	);
};

export default PackagePaymentModal;
